import React from "react";
import { API_BASE_URL } from "../../utils/api";

const ArchitecturalRequestPrintContent = ({
  requestDetails,
  member,
  comments = [],
  contractor,
  colors = [],
  files = [],
  filesLoading = false,
}) => {
  if (!requestDetails) return null;

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString() : "N/A";

  const formatDateTime = (date) =>
    date ? new Date(date).toLocaleString() : "N/A";

  const safeMember = member || {};
  const firstName = safeMember.first_name || safeMember.FirstName || "N/A";
  const lastName = safeMember.last_name || safeMember.LastName || "";
  const email = safeMember.email || safeMember.EmailAddress || "N/A";
  const phone = safeMember.phone_number || safeMember.PhoneNumber || "N/A";
  const altPhone = safeMember.AlternatePhoneNumber || "N/A";
  const address =
    safeMember.property_name ||
    safeMember.FullAddress ||
    safeMember.Address ||
    "N/A";
  const memberCode = safeMember.ClubMemberCode || "N/A";
  const birthDate = safeMember.BirthDate
    ? formatDate(safeMember.BirthDate)
    : "N/A";
  const joinDate = safeMember.JoinDate ? formatDate(safeMember.JoinDate) : "N/A";

  const mapsLink =
    address && address !== "N/A"
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          address
        )}`
      : null;

  const acknowledged =
    requestDetails?.acknowledged === 0 ||
    requestDetails?.acknowledged === "0" ||
    requestDetails?.acknowledged === false;

  const completionDays = requestDetails?.completion_days_current || null;
  const hasConditions =
    requestDetails?.conditions &&
    String(requestDetails.conditions).trim().length > 0;

  const getFileName = (file) =>
    file?.file_name ||
    file?.filename ||
    file?.name ||
    file?.file_url ||
    "Attachment";

  const getFileUrl = (file) => {
    const path = file?.file_url || file?.url || file?.src || "";
    if (!path) return "";
    if (String(path).startsWith("http")) return path;
    return `${API_BASE_URL}${path}`;
  };

  const isImage = (file) => {
    if (file?.file_type) {
      return String(file.file_type).startsWith("image/");
    }

    const name = getFileName(file);
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name);
  };

  const imageFiles = (files || []).filter((file) => isImage(file));
  const otherFiles = (files || []).filter((file) => !isImage(file));

  return (
    <div style={styles.page}>
      <style>{`
        @media print {
          @page {
            margin: 32px 24px;
          }

          html, body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>

      <div style={styles.container}>
        <header style={styles.header}>
          
          <h1 style={styles.title}>Architectural Request Summary</h1>
          <p style={styles.generated}>Generated on {formatDateTime(new Date())}</p>
        </header>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Acknowledgement</h2>
          <div
            style={{
              ...styles.statusBanner,
              ...(acknowledged ? styles.statusSuccess : styles.statusWarning),
            }}
          >
            <span style={styles.statusIcon}>{acknowledged ? "✅" : "⚠️"}</span>
            <span>
              {acknowledged
                ? "Member has acknowledged the architectural request terms."
                : "Member has not acknowledged the terms yet."}
            </span>
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Request Overview</h2>
          <div style={styles.grid}>
            <div style={styles.field}>
              <span style={styles.label}>ID:</span> #{requestDetails.id || "N/A"}
            </div>
            <div style={styles.field}>
              <span style={styles.label}>Status:</span>{" "}
              {requestDetails.status || "N/A"}
            </div>
            <div style={styles.field}>
              <span style={styles.label}>Priority:</span>{" "}
              {requestDetails.priority || "N/A"}
            </div>
            <div style={styles.field}>
              <span style={styles.label}>Requested At:</span>{" "}
              {formatDateTime(requestDetails.requested_at)}
            </div>
            <div style={styles.field}>
              <span style={styles.label}>Type:</span>{" "}
              {requestDetails.type_name || "N/A"}
            </div>
            <div style={styles.field}>
              <span style={styles.label}>Subtype:</span>{" "}
              {requestDetails.subtype_name || "N/A"}
            </div>
            <div style={styles.field}>
              <span style={styles.label}>Anticipated Start:</span>{" "}
              {formatDate(requestDetails.anticipated_start_date)}
            </div>
            <div style={styles.field}>
              <span style={styles.label}>Anticipated Completion:</span>{" "}
              {formatDate(requestDetails.anticipated_completion_date)}
            </div>
            <div style={styles.field}>
              <span style={styles.label}>Property ID:</span>{" "}
              {requestDetails.property_id || "N/A"}
            </div>
            <div style={styles.field}>
              <span style={styles.label}>Assigned To:</span>{" "}
              {requestDetails.assigned_user_name || "Unassigned"}
            </div>
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Owner Information</h2>
          <div style={styles.grid}>
            <div style={styles.field}>
              <span style={styles.label}>Name:</span> {firstName} {lastName}
            </div>
            <div style={styles.field}>
              <span style={styles.label}>Member Code:</span> {memberCode}
            </div>
            
           
            <div style={styles.field}>
              <span style={styles.label}>Email:</span> {email}
            </div>
            <div style={styles.field}>
              <span style={styles.label}>Phone:</span> {phone}
            </div>
            <div style={styles.field}>
              <span style={styles.label}>Alternate Phone:</span> {altPhone}
            </div>
            <div style={styles.field}>
              <span style={styles.label}>Address:</span>{" "}
              {mapsLink ? (
                <a
                  href={mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.link}
                >
                  {address}
                </a>
              ) : (
                address
              )}
            </div>
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Contractor Information</h2>
          {contractor ? (
            <div style={styles.grid}>
              <div style={styles.field}>
                <span style={styles.label}>Company:</span>{" "}
                {contractor.company_name || "N/A"}
              </div>
              <div style={styles.field}>
                <span style={styles.label}>Contact:</span>{" "}
                {contractor.contractor_name || "N/A"}
              </div>
              <div style={styles.field}>
                <span style={styles.label}>Phone:</span>{" "}
                {contractor.phone_number || "N/A"}
              </div>
              <div style={styles.field}>
                <span style={styles.label}>Notes:</span>{" "}
                {contractor.notes || "N/A"}
              </div>
            </div>
          ) : (
            <div style={styles.emptyState}>No contractor information added.</div>
          )}
        </section>

        {colors?.length > 0 && (
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Color Selection Details</h2>
            <div style={styles.cardList}>
              {colors.map((color) => (
                <div key={color.id} style={styles.colorRow}>
                  <div style={{ flex: 1 }}>
                    <div style={styles.cardTitle}>
                      {color.scheme_name || "Unnamed Scheme"}
                    </div>
                    <div style={styles.cardText}>
                      <strong>Subtype:</strong> {color.subtype_name || "N/A"}
                    </div>
                    <div style={styles.cardText}>
                      <strong>Color Name:</strong>{" "}
                      {color.option_color_name || color.scheme_name || "N/A"}
                    </div>
                    <div style={styles.metaText}>
                      <strong>Created:</strong>{" "}
                      {color.created_at
                        ? new Date(color.created_at).toLocaleString()
                        : "N/A"}
                    </div>
                  </div>

                  {color.scheme_hex && (
                    <div
                      style={{
                        ...styles.colorBox,
                        backgroundColor: color.scheme_hex,
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {requestDetails?.freeform_responses?.length > 0 && (
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Freeform Responses</h2>
            <div style={styles.cardList}>
              {requestDetails.freeform_responses.map((response) => (
                <div key={response.id} style={styles.card}>
                  <div style={styles.cardTitle}>
                    {response.subtype_name || "N/A"}
                  </div>
                  <div style={styles.cardText}>
                    <strong>Type:</strong> {response.type_name || "N/A"}
                  </div>
                  <div style={{ ...styles.cardText, whiteSpace: "pre-line" }}>
                    <strong>Response:</strong>{" "}
                    {response.field_content || "No response provided."}
                  </div>
                  <div style={styles.metaText}>
                    Added on{" "}
                    {response.created_at
                      ? new Date(response.created_at).toLocaleString()
                      : "N/A"}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Description</h2>
          <div style={styles.noteBox}>
            {requestDetails.description || "No description provided."}
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Conditions</h2>
          <div style={styles.noteBox}>
            {hasConditions ? requestDetails.conditions : "N/A"}
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Completion Days</h2>
          {completionDays ? (
            <>
              <div style={styles.grid}>
                <div style={styles.field}>
                  <span style={styles.label}>Days to Complete:</span>{" "}
                  {completionDays.days_to_complete ?? "N/A"}
                </div>
                <div style={styles.field}>
                  <span style={styles.label}>Recorded At:</span>{" "}
                  {completionDays.recorded_at
                    ? formatDateTime(completionDays.recorded_at)
                    : "N/A"}
                </div>
              </div>

              <div style={{ ...styles.noteBox, marginTop: 12 }}>
                <strong>Notes:</strong>{" "}
                {completionDays.notes && String(completionDays.notes).trim()
                  ? completionDays.notes
                  : "N/A"}
              </div>
            </>
          ) : (
            <div style={styles.emptyState}>No completion days recorded.</div>
          )}
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Attachments</h2>

          {filesLoading ? (
            <div style={styles.emptyState}>Loading attachments...</div>
          ) : files.length === 0 ? (
            <div style={styles.emptyState}>No attachments available.</div>
          ) : (
            <>
              {imageFiles.length > 0 && (
                <div style={{ marginBottom: otherFiles.length > 0 ? 16 : 0 }}>
                  <div style={styles.attachmentsSubTitle}>Image Attachments</div>
                  <div style={styles.imageGrid}>
                    {imageFiles.map((file, index) => {
                      const src = getFileUrl(file);
                      const name = getFileName(file);

                      return (
                        <div
                          key={file.id || file.file_id || index}
                          style={styles.imageCard}
                        >
                          <img src={src} alt={name} style={styles.image} />
                          <div style={styles.imageCaption}>{name}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {otherFiles.length > 0 && (
                <div>
                  <div style={styles.attachmentsSubTitle}>Other Attachments</div>
                  <div style={styles.fileList}>
                    {otherFiles.map((file, index) => (
                      <div
                        key={file.id || file.file_id || index}
                        style={styles.fileListItem}
                      >
                        {getFileName(file)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Comments</h2>
          {comments.length === 0 ? (
            <div style={styles.emptyState}>No comments recorded.</div>
          ) : (
            <div style={styles.cardList}>
              {comments.map((c) => (
                <div key={c.comment_id} style={styles.card}>
                  <div style={styles.commentHeader}>
                    <strong>
                      {c.user_name} {c.user_lastname}
                    </strong>{" "}
                    -{" "}
                    <span style={{ color: "#6b778c" }}>
                      {formatDateTime(c.sent_at)}
                    </span>
                  </div>
                  <div style={styles.commentText}>{c.comment_text}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        <footer style={styles.footer}>
          Architectural Request Summary Report
        </footer>
      </div>
    </div>
  );
};

const styles = {
  page: {
    backgroundColor: "#fff",
    width: "100%",
  },
  container: {
    fontFamily: "'Segoe UI', Arial, sans-serif",
    color: "#223142",
    padding: "20px 24px",
    lineHeight: 1.45,
    fontSize: "14px",
    backgroundColor: "#fff",
  },
  logo: {
    width: 200,
    height: 90,
    objectFit: "contain",
    marginBottom: 8,
  },
  header: {
    textAlign: "center",
    borderBottom: "2px solid #d9e2f0",
    paddingBottom: "14px",
    marginBottom: "24px",
  },
  title: {
    margin: "8px 0 4px",
    fontSize: "26px",
    fontWeight: 700,
    color: "#2f63ad",
  },
  generated: {
    fontSize: "13px",
    color: "#6b778c",
    margin: 0,
  },
  section: {
    marginBottom: "22px",
    padding: "16px 18px",
    border: "1px solid #dfe7f3",
    borderRadius: "12px",
    backgroundColor: "#fafcff",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#223142",
    borderBottom: "1px solid #d9e2f0",
    paddingBottom: "6px",
    marginBottom: "14px",
  },
  attachmentsSubTitle: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#2f63ad",
    marginBottom: "10px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "10px 24px",
  },
  field: {
    wordBreak: "break-word",
    whiteSpace: "normal",
  },
  label: {
    fontWeight: 700,
    color: "#2f63ad",
    marginRight: 6,
  },
  link: {
    color: "#2f63ad",
    textDecoration: "none",
    fontWeight: 500,
  },
  statusBanner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    borderRadius: "10px",
    fontWeight: 600,
  },
  statusSuccess: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    border: "1px solid #c8e6c9",
  },
  statusWarning: {
    backgroundColor: "#fff3e0",
    color: "#ef6c00",
    border: "1px solid #ffe0b2",
  },
  statusIcon: {
    fontSize: 16,
    lineHeight: 1,
  },
  noteBox: {
    backgroundColor: "#f5f9ff",
    borderLeft: "4px solid #2f63ad",
    padding: "12px 14px",
    borderRadius: "6px",
    color: "#334155",
    whiteSpace: "pre-line",
  },
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  card: {
    border: "1px solid #dde6f2",
    borderRadius: "10px",
    padding: "12px 14px",
    backgroundColor: "#fff",
  },
  cardTitle: {
    fontWeight: 700,
    marginBottom: 6,
    color: "#223142",
  },
  cardText: {
    fontSize: "13.5px",
    color: "#333",
    marginBottom: 4,
  },
  metaText: {
    fontSize: "12px",
    color: "#6b778c",
    marginTop: 6,
  },
  colorRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    border: "1px solid #dde6f2",
    borderRadius: "10px",
    padding: "12px 14px",
    backgroundColor: "#fff",
  },
  colorBox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    border: "1px solid #999",
    flexShrink: 0,
  },
  imageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: "12px",
  },
  imageCard: {
    border: "1px solid #dde6f2",
    borderRadius: "10px",
    padding: "8px",
    backgroundColor: "#fff",
    textAlign: "center",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  },
  image: {
    width: "100%",
    height: "auto",
    maxHeight: "220px",
    objectFit: "contain",
    borderRadius: "6px",
    display: "block",
    margin: "0 auto",
  },
  imageCaption: {
    fontSize: "12px",
    color: "#6b778c",
    marginTop: "6px",
    wordBreak: "break-word",
  },
  fileList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  fileListItem: {
    border: "1px solid #dde6f2",
    borderRadius: "8px",
    padding: "10px 12px",
    backgroundColor: "#fff",
    color: "#223142",
    wordBreak: "break-word",
  },
  commentHeader: {
    fontSize: "13px",
    marginBottom: "6px",
  },
  commentText: {
    color: "#333",
    whiteSpace: "pre-line",
  },
  emptyState: {
    color: "#6b778c",
    fontStyle: "italic",
  },
  footer: {
    textAlign: "center",
    fontSize: "12px",
    color: "#6b778c",
    marginTop: "28px",
    borderTop: "1px solid #d9e2f0",
    paddingTop: "12px",
  },
};

export default ArchitecturalRequestPrintContent;